import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadFaceImage, registerStudent } from '../utils/api';

export default function RegisterStudent() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', rollNo: '', class: '', section: '', gender: '', dob: '', contact: '' });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // prefer the new key 'capturedFace', but fall back to legacy 'capturedStudentFace'
    const img = sessionStorage.getItem('capturedFace') || sessionStorage.getItem('capturedStudentFace');
    if (img) {
      setImagePreview(img);
    }
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    try {
      let embedding = null;
      // if there's a captured image in sessionStorage, upload it to get embedding
      const img = sessionStorage.getItem('capturedFace') || sessionStorage.getItem('capturedStudentFace');
      if (img) {
        // img is a data URL (base64). Send it directly to uploadFaceImage which will POST JSON { image }
        const faceResp = await uploadFaceImage(img);
        // faceResp expected to include { embedding: [...] }
        if (faceResp && faceResp.embedding) {
          embedding = faceResp.embedding;
        }
      }

      // build payload - include embedding if available
      const payload = {
        ...form,
      };
      if (embedding) payload.faceEmbedding = embedding;

      await registerStudent(payload);
      // clear captured image keys
      sessionStorage.removeItem('capturedStudentFace');
      sessionStorage.removeItem('capturedFace');
      alert('Student registered successfully');
      navigate('/students');
    } catch (err) {
      alert('Registration failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold">Register Student</h2>
              <p className="text-sm text-gray-600 mt-1">Add a student and capture their face for attendance.</p>
            </div>
            <div>
              <button onClick={() => navigate('/students')} className="px-4 py-2 bg-gray-200 rounded">Back to list</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <form onSubmit={handleSave} className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Full name</label>
                <input required name="name" value={form.name} onChange={handleChange} placeholder="Full name" className="mt-1 w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Roll No</label>
                  <input type="number" name="rollNo" value={form.rollNo} onChange={handleChange} placeholder="Roll No" className="mt-1 w-full p-3 border rounded-md" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Class</label>
                  <select name="class" value={form.class} onChange={handleChange} className="mt-1 w-full p-3 border rounded-md bg-white">
                    <option value="">Select class</option>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(c => (
                      <option key={c} value={String(c)}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Section</label>
                  <select name="section" value={form.section} onChange={handleChange} className="mt-1 w-full p-3 border rounded-md bg-white">
                    <option value="">Select section</option>
                    {['A','B','C','D'].map(s => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Gender</label>
                  <select name="gender" value={form.gender} onChange={handleChange} className="mt-1 w-full p-3 border rounded-md bg-white">
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">DOB</label>
                  <input type="date" name="dob" value={form.dob} onChange={handleChange} className="mt-1 w-full p-3 border rounded-md" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Contact</label>
                <input name="contact" value={form.contact} onChange={handleChange} placeholder="Contact number" className="mt-1 w-full p-3 border rounded-md" />
              </div>

              <div className="flex items-center gap-3">
                <button type="submit" disabled={loading} className="px-6 py-3 bg-green-600 text-white rounded-lg">{loading ? 'Saving...' : 'Save'}</button>
                <button type="button" onClick={() => navigate('/students')} className="px-4 py-3 bg-gray-200 rounded-lg">Cancel</button>
                <button type="button" onClick={() => navigate('/capturestudentface')} className="ml-auto px-6 py-3 bg-blue-600 text-white rounded-lg">Capture Face</button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="mb-4">
              <h3 className="text-lg font-medium">Face preview</h3>
              <p className="text-sm text-gray-500">Captured face will appear here for verification.</p>
            </div>
            {imagePreview ? (
              <img src={imagePreview} alt="preview" className="w-full rounded-lg border" />
            ) : (
              <div className="p-6 border rounded text-sm text-gray-500">No face captured. Use <button type="button" onClick={() => navigate('/capturestudentface')} className="text-blue-600 underline">Capture Face</button></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
